import { ErrorMessage, useFormikContext } from 'formik';
import { ChevronDown, SearchIcon } from 'lucide-react';

import { Checkbox } from '@src/design-system/checkbox';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Label } from '@src/design-system/label';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import programApi from '@src/features/programs/services/program-api';
import { PROGRAM_STATUSES, type TProgram } from '@src/shared/types/program';

import {
  ANNOUNCEMENT_STATIC_AUDIENCE_OPTIONS,
  getShareToDisplayLabel,
} from '../../../utils/announcement-audience';
import type { TAnnouncementFormValues } from '../schema';

const PROGRAM_SEARCH_PARAM = 'announcement_program_search';

export function AnnouncementShareToSelect() {
  const { values, setFieldValue } = useFormikContext<TAnnouncementFormValues>();

  const selectStaticAudience = (
    key: (typeof ANNOUNCEMENT_STATIC_AUDIENCE_OPTIONS)[number]['key']
  ) => {
    const isSelected = values.audience === key;

    setFieldValue('audience', isSelected ? '' : key);
    setFieldValue('shareToPrograms', []);
  };

  const toggleProgram = (program: TProgram) => {
    const current = values.shareToPrograms;
    const exists = current.some((item) => item.id === program.id);
    const nextPrograms = exists
      ? current.filter((item) => item.id !== program.id)
      : [...current, { id: program.id, name: program.name }];

    setFieldValue('shareToPrograms', nextPrograms);
    setFieldValue('audience', nextPrograms.length ? 'PROGRAMS' : '');
  };

  const displayLabel = getShareToDisplayLabel(
    values.audience,
    values.shareToPrograms
  );

  const hasSelection =
    Boolean(values.audience) &&
    (values.audience !== 'PROGRAMS' || values.shareToPrograms.length > 0);

  return (
    <div className='flex flex-col gap-2'>
      <Label>Share to</Label>

      <Popover modal>
        <PopoverTrigger
          type='button'
          className={cn(
            inputVariants(),
            'w-full flex justify-between items-center cursor-pointer text-sm',
            !hasSelection && 'text-gray-500'
          )}
        >
          <span className='truncate text-left'>{displayLabel}</span>
          <ChevronDown
            size={16}
            className='shrink-0 text-gray-800'
          />
        </PopoverTrigger>

        <PopoverContent
          withPortal={false}
          align='start'
          style={{ width: 'var(--radix-popover-trigger-width)' }}
          className='p-0'
        >
          <InfiniteList
            enableSearch
            searchParamName={PROGRAM_SEARCH_PARAM}
            fetchInfiniteData={programApi.useFetchInfinite}
            query={{ status: PROGRAM_STATUSES.active }}
            allItem={
              <>
                {ANNOUNCEMENT_STATIC_AUDIENCE_OPTIONS.map((option) => {
                  const isSelected = values.audience === option.key;

                  return (
                    <label
                      key={option.key}
                      className='flex min-h-12 cursor-pointer items-center gap-2 border-b border-primary-200 px-4 py-2 hover:bg-primary-30'
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => selectStaticAudience(option.key)}
                        className='border border-primary-200'
                      />
                      <span className='truncate text-sm text-gray-900'>
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </>
            }
            props={{
              searchContainerProps: { className: 'p-3 pb-0 mb-0' },
              searchInputProps: {
                placeholder: 'Search programs...',
                prefixIcon: (
                  <SearchIcon className='size-4 shrink-0 text-gray-800' />
                ),
                className: 'bg-primary-30',
              },
              listContainerProps: {
                className: 'max-h-[240px] min-h-0 p-0 pt-0',
              },
              listProps: { className: 'gap-0' },
            }}
            renderItem={(program) => {
              const isSelected =
                values.audience === 'PROGRAMS' &&
                values.shareToPrograms.some((item) => item.id === program.id);

              return (
                <label
                  key={program.id}
                  className='flex min-h-12 cursor-pointer items-center gap-2 border-b border-primary-200 px-4 py-2 hover:bg-primary-30'
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleProgram(program)}
                    className='border border-primary-200'
                  />
                  <span
                    className='truncate text-sm text-gray-900'
                    title={program.name}
                  >
                    {program.name}
                  </span>
                </label>
              );
            }}
          />
        </PopoverContent>
      </Popover>

      <ErrorMessage
        name='audience'
        component={CustomErrorMessage}
      />
    </div>
  );
}
